// src/components/goal-settings/KpaModal.jsx
import React, { useEffect, useState } from 'react';
import { listTimeframes } from '../../api/lookups';

/**
 * Props expected:
 * - show/open/visible: boolean (any will open the modal)
 * - kpa: {
 *     id, name, weight, timeframe_id, alignment, description,
 *     timeframe?: { id, name }, parent_id?: number|null
 *   }
 * - onClose(): void
 * - onSaved(payload): Promise|void  // parent (KpaList) will call updateKpa(kpa.id, payload)
 *
 * Payload shape we emit:
 *   { name, weight, timeframe_id (nullable), alignment, description }
 */
export default function KpaModal({
                                     show, open, visible,
                                     kpa,
                                     onClose,
                                     onSaved,
                                 }) {
    const isOpen = !!(show ?? open ?? visible);

    const [name, setName] = useState('');
    const [weight, setWeight] = useState('');
    const [timeframeId, setTimeframeId] = useState('');
    const [alignment, setAlignment] = useState('');
    const [description, setDescription] = useState('');

    const [timeframes, setTimeframes] = useState([]);
    const [loadingTF, setLoadingTF] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    // Load timeframes on open
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                setLoadingTF(true);
                const rows = await listTimeframes();
                setTimeframes(rows);
            } catch {
                setTimeframes([]);
            } finally {
                setLoadingTF(false);
            }
        })();
    }, [isOpen]);

    // Prefill fields when opened / when kpa changes
    useEffect(() => {
        if (!isOpen || !kpa) return;

        setName(kpa.name ?? '');
        setWeight(
            kpa.weight != null && !Number.isNaN(Number(kpa.weight))
                ? String(kpa.weight)
                : ''
        );
        // Prefer kpa.timeframe_id; fallback to nested kpa.timeframe.id; otherwise blank
        const tfId = kpa.timeframe_id ?? kpa?.timeframe?.id ?? '';
        setTimeframeId(tfId ? String(tfId) : '');
        setAlignment(kpa.alignment ?? '');
        setDescription(kpa.description ?? '');
        setErr('');
        setSaving(false);
    }, [isOpen, kpa]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) return setErr('Name is required.');
        if (!weight || !Number.isFinite(Number(weight))) return setErr('Weight must be a number.');

        try {
            setSaving(true);
            setErr('');
            const payload = {
                name: name.trim(),
                weight: Number(weight),
                timeframe_id: timeframeId ? Number(timeframeId) : null,
                alignment: alignment?.trim() || null,
                description: description?.trim() || null,
            };
            await onSaved?.(payload);
            onClose?.();
        } catch (e2) {
            setErr(e2?.response?.data?.message || 'Failed to update KPA.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const cascaded = Boolean(kpa?.parent_id);

    return (
        <div
            className="modal show"
            style={{ display: 'block', background: 'rgba(0,0,0,.4)' }}
            aria-modal="true"
            role="dialog"
        >
            <div className="modal-dialog modal-md">
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h6 className="modal-title">
                            Edit KPA {cascaded && <span className="badge bg-secondary ms-2">Cascaded</span>}
                        </h6>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <form onSubmit={submit}>
                        <div className="modal-body">
                            {err && <div className="alert alert-danger py-1">{err}</div>}

                            <div className="mb-2">
                                <label className="form-label mb-1">Name <span className="text-danger">*</span></label>
                                <input
                                    className="form-control form-control-sm"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Deliver 3 product releases"
                                    autoFocus
                                />
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <div className="mb-2">
                                        <label className="form-label mb-1">Weight <span className="text-danger">*</span></label>
                                        <input
                                            className="form-control form-control-sm"
                                            type="number"
                                            step="0.01"
                                            value={weight}
                                            onChange={(e) => setWeight(e.target.value)}
                                            placeholder="e.g., 30"
                                        />
                                    </div>
                                </div>

                                <div className="col-6">
                                    <div className="mb-2">
                                        <label className="form-label mb-1">Timeframe</label>
                                        <select
                                            className="form-select form-select-sm"
                                            disabled={loadingTF}
                                            value={timeframeId}
                                            onChange={(e) => setTimeframeId(e.target.value)}
                                        >
                                            <option value="">— Select —</option>
                                            {timeframes.map(tf => (
                                                <option key={tf.id} value={tf.id}>{tf.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-2">
                                <label className="form-label mb-1">Alignment</label>
                                <input
                                    className="form-control form-control-sm"
                                    value={alignment}
                                    onChange={(e) => setAlignment(e.target.value)}
                                    placeholder="How this aligns with strategy"
                                />
                            </div>

                            <div className="mb-1">
                                <label className="form-label mb-1">Description</label>
                                <textarea
                                    className="form-control form-control-sm"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Optional details..."
                                />
                            </div>

                            {cascaded && (
                                <div className="alert alert-info py-1">
                                    This KPA was cascaded from the manager. You can rename or adjust fields as needed.
                                </div>
                            )}
                        </div>

                        <div className="modal-footer py-2">
                            <button type="button" className="btn btn-sm btn-light" onClick={onClose} disabled={saving}>Cancel</button>
                            <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </form>

                </div>
            </div>
        </div>
    );
}